import { Button } from '@/components/common/Button';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { Label } from '@/components/common/Label';
import { RadioGroup, RadioGroupItem } from '@/components/common/RadioGroup';
import { Textarea } from '@/components/common/Textarea';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { FORUM, ROUTES, SEO_ENTITY_ID, SEO_ENTITY_TYPE, SUPPORTED_FORMATS } from '@/configs/constants';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/common/Select';
import { useStore } from '@/hooks/useStore';
import { flowResult, toJS } from 'mobx';
import { toastify } from '@/utils/toastify';
import { useEffect, useMemo, useRef, useState } from 'react';
import { GetSeoResponse } from '@/types/http-payload/forum';
import { useNavigate, useParams } from 'react-router-dom';
import { uploadFileToS3UsingPresignedPost } from '@/utils/utils';
import { BanIcon, EllipsisIcon, ImagePlusIcon, SquarePenIcon, Trash2Icon, BookmarkIcon, RefreshCcw } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/common/DropdownMenu';
import { ActionDropdownItem } from '@/components/general/ActionDropdownItem';
import Tooltip from '@/components/common/Tooltip';

const BANNER_STATUS = {
    PUBLISHED: 0,
    UNPUBLISHED: 1,
    UNSAVED: 2
};

type ActionMenuDropdownProps = {
    banner: any,
    setDefaultBanner: (id: string) => void,
    onTogglePublishBanner: (id: string) => void,
    onDeleteBanner: (id: string) => void
};

const ActionMenuDropdown = ({ banner, onDeleteBanner, onTogglePublishBanner, setDefaultBanner }: ActionMenuDropdownProps) => {
    // hooks
    const { t } = useTranslation();

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <EllipsisIcon className='h-6 w-6 text-active hover:cursor-pointer' />
            </DropdownMenuTrigger>

            <DropdownMenuContent className='w-48' align='end' forceMount>
                {
                    !banner.isDefault && banner.status !== BANNER_STATUS.UNSAVED ?
                        (
                            <>
                                <DropdownMenuItem className='hover:cursor-pointer' asChild>
                                    <ActionDropdownItem icon={BookmarkIcon} text={t('words_title.set_default')} onClick={() => setDefaultBanner(banner.id)} />
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                            </>
                        ) :
                        null
                }
                {
                    banner.status === BANNER_STATUS.UNPUBLISHED ?
                        (
                            <>
                                <DropdownMenuItem className='hover:cursor-pointer' asChild>
                                    <ActionDropdownItem icon={RefreshCcw} text={t('words_title.publish')} onClick={() => onTogglePublishBanner(banner.id)} />
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                            </>
                        ) :
                        null
                }
                {
                    banner.status === BANNER_STATUS.PUBLISHED ?
                        (
                            <>
                                <DropdownMenuItem className='hover:cursor-pointer' asChild>
                                    <ActionDropdownItem icon={RefreshCcw} text={t('words_title.unpublish')} onClick={() => onTogglePublishBanner(banner.id)} />
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                            </>
                        ) :
                        null
                }

                <DropdownMenuItem className='hover:cursor-pointer' asChild>
                    <ActionDropdownItem icon={Trash2Icon} text={t('words_title.delete_action')} onClick={() => onDeleteBanner(banner.id)} />
                </DropdownMenuItem>

            </DropdownMenuContent>
        </DropdownMenu>
    );
};

const renderImageStatus = (status: number, t) => {
    switch (status) {
        case BANNER_STATUS.PUBLISHED:
            return (
                <div className='text-[#077D07] border border-[#077D07] text-[13px] px-2 rounded-lg font-semibold flex justify-center items-center gap-1'>
                    <svg width='13' height='9' viewBox='0 0 13 9' fill='none' xmlns='http://www.w3.org/2000/svg'>
                        <path d='M10.2917 7.12848V8.21181H3.79166V7.12848C3.79166 7.12848 3.79166 4.96181 7.04165 4.96181C10.2917 4.96181 10.2917 7.12848 10.2917 7.12848ZM8.66665 2.25348C8.66665 1.93209 8.57135 1.61791 8.39279 1.35068C8.21423 1.08345 7.96044 0.875168 7.66352 0.752176C7.36659 0.629183 7.03985 0.597003 6.72463 0.659704C6.40941 0.722405 6.11987 0.877171 5.89261 1.10443C5.66535 1.33169 5.51058 1.62124 5.44788 1.93646C5.38518 2.25168 5.41736 2.57841 5.54035 2.87534C5.66334 3.17227 5.87162 3.42606 6.13885 3.60462C6.40608 3.78318 6.72026 3.87848 7.04165 3.87848C7.47263 3.87848 7.88596 3.70728 8.1907 3.40253C8.49545 3.09778 8.66665 2.68446 8.66665 2.25348ZM10.4 4.99431C10.6961 5.26748 10.9348 5.59689 11.1022 5.96331C11.2696 6.32973 11.3623 6.72583 11.375 7.12848V8.21181H13V7.12848C13 7.12848 13 5.25973 10.4 4.99431ZM9.74999 0.62848C9.58632 0.628489 9.42367 0.654076 9.2679 0.704313C9.58481 1.15875 9.75472 1.69945 9.75472 2.25348C9.75472 2.80751 9.58481 3.3482 9.2679 3.80265C9.42367 3.85288 9.58632 3.87847 9.74999 3.87848C10.181 3.87848 10.5943 3.70728 10.899 3.40253C11.2038 3.09778 11.375 2.68446 11.375 2.25348C11.375 1.8225 11.2038 1.40918 10.899 1.10443C10.5943 0.799685 10.181 0.62848 9.74999 0.62848ZM3.97582 2.75181L4.60416 3.51556L2.03124 6.08848L0.541656 4.46348L1.16999 3.83515L2.03124 4.69098L3.97582 2.75181Z' fill='#077D07' />
                    </svg>
                    {t('words_title.published')}
                </div>
            );
        case BANNER_STATUS.UNPUBLISHED:
            return (
                <span className='text-[#555555] border border-transparent bg-[#EDEDED] text-[13px] px-2 rounded-lg font-semibold flex justify-center items-center gap-1'>
                    <svg width='13' height='8' viewBox='0 0 13 8' fill='none' xmlns='http://www.w3.org/2000/svg'>
                        <path d='M13 6.54865V7.63198H11.375V6.54865C11.375 5.70906 10.9958 4.95615 10.3892 4.41448C13 4.6799 13 6.54865 13 6.54865ZM9.75001 0.048645C10.6492 0.048645 11.375 0.774478 11.375 1.67365C11.3753 1.92886 11.3154 2.18056 11.2002 2.40828C11.085 2.63601 10.9177 2.83335 10.7119 2.98428C10.5061 3.1352 10.2675 3.23546 10.0157 3.27691C9.76389 3.31835 9.50583 3.29982 9.26251 3.22281C9.57126 2.78406 9.75001 2.25323 9.75001 1.67365C9.75001 1.09948 9.57126 0.563228 9.26251 0.124478C9.41418 0.0757283 9.58209 0.048645 9.75001 0.048645ZM7.04168 0.048645C7.94084 0.048645 8.66668 0.774478 8.66668 1.67365C8.66668 2.57281 7.94084 3.29865 7.04168 3.29865C6.14251 3.29865 5.41668 2.57281 5.41668 1.67365C5.41668 0.774478 6.14251 0.048645 7.04168 0.048645ZM10.2917 6.54865V7.63198H3.79168V6.54865C3.79168 5.35156 5.24876 4.38198 7.04168 4.38198C8.83459 4.38198 10.2917 5.35156 10.2917 6.54865ZM0.251343 4.44698L1.40293 3.29865L0.251343 2.15031L1.01834 1.38115L2.16668 2.5349L3.31501 1.38115L4.08418 2.15031L2.93043 3.29865L4.08418 4.44698L3.31501 5.21615L2.16668 4.0624L1.01834 5.21615L0.251343 4.44698Z' fill='#555555' />
                    </svg>
                    {t('words_title.unpublished')}
                </span>
            );
        case BANNER_STATUS.UNSAVED:
            return (
                <span className='text-[#DB0900] border border-[#DB0900]  text-[13px] px-2 rounded-lg font-semibold flex justify-center items-center gap-1'>
                    <svg width='13' height='10' viewBox='0 0 13 10' fill='none' xmlns='http://www.w3.org/2000/svg'>
                        <path d='M8.12499 0.125H1.62499C1.02916 0.125 0.541656 0.6125 0.541656 1.20833V8.79167C0.541656 9.3875 1.02916 9.875 1.62499 9.875H9.20832C9.80416 9.875 10.2917 9.3875 10.2917 8.79167V2.29167L8.12499 0.125ZM9.20832 8.79167H1.62499V1.20833H7.69166L9.20832 2.725V8.79167ZM5.41666 5C4.49582 5 3.79166 5.70417 3.79166 6.625C3.79166 7.54583 4.49582 8.25 5.41666 8.25C6.33749 8.25 7.04166 7.54583 7.04166 6.625C7.04166 5.70417 6.33749 5 5.41666 5ZM2.16666 1.75H7.04166V3.91667H2.16666V1.75ZM12.4583 5.54167H11.375V2.29167H12.4583V5.54167ZM12.4583 7.70833H11.375V6.625H12.4583V7.70833Z' fill='#DA251D' />
                    </svg>
                    {t('words_title.unsaved')}
                </span>
            );
        default:
            return <div></div>;
    }
};

const renderDefaultBanner = (banner: any, t) => {
    if (banner.isDefault) {
        return (
            <div className='flex items-center gap-1 text-[13px] text-[#777777]'>
                <svg width='9' height='12' viewBox='0 0 9 12' fill='none' xmlns='http://www.w3.org/2000/svg'>
                    <path d='M7.82792 0.340332H0.828314C0.553314 0.340332 0.327728 0.56377 0.327728 0.840918V11.3403L4.32812 7.33994L8.32851 11.3403V0.840918C8.32851 0.56377 8.10507 0.340332 7.82792 0.340332Z' fill='#777777' />
                </svg>
                <span className='-translate-y-[1px]'>{t('words_title.default')}</span>
            </div>
        );
    }
    return <></>;
};

interface IBanner {
    id: string,
    name: string,
    image: string,
    key?: string,
    isDefault: boolean,
    status: number,
    alt: string,
    createdAt?: string,
    file?: File
}

export default observer(function BannerSetting() {
    const { t } = useTranslation();
    const [previewImage, setPreviewImage] = useState<string | null>(null);
    const labelImagesRef = useRef<HTMLLabelElement>(null);
    const [bannerList, setBannerList] = useState<IBanner[]>([]);

    const {
        generalSettingStore: { getBanner, updateBanner, getPresignedUrl },
        modalStore: { showAlertModal, hideModal }
    } = useStore();

    useEffect(() => {
        refetchData();
    }, []);

    const refetchData = async () => {
        const res: any = await getBanner();

        setBannerList(res);
    };

    const navigate = useNavigate();

    const onChangeAlt = (id: string, value: string) => {
        setBannerList(prev => prev.map(banner => banner.id === id ? { ...banner, alt: value } : banner));
    };

    const setDefaultBanner = (id: string) => {
        setBannerList(prev =>
            prev.map(banner =>
                banner.id === id ?
                    { ...banner, isDefault: true } :
                    { ...banner, isDefault: false }));
    };

    const onTogglePublishBanner = (id: string) => {
        setBannerList((prev) => {
            const updatedList = prev.map(banner =>
                banner.id === id ?
                    {
                        ...banner,
                        status:
                            banner.status === BANNER_STATUS.PUBLISHED ?
                                BANNER_STATUS.UNPUBLISHED :
                                BANNER_STATUS.PUBLISHED
                    } :
                    banner);

            // Nếu chỉ còn 1 banner thì không thay đổi default
            if (updatedList.length === 1) {
                return updatedList;
            }

            // Nếu banner bị unpublish là default thì chọn banner khác làm default
            const toggledBanner = updatedList.find(banner => banner.id === id);
            if (
                toggledBanner &&
                toggledBanner.status === BANNER_STATUS.UNPUBLISHED &&
                toggledBanner.isDefault
            ) {
                // Chọn banner đầu tiên còn lại (không phải banner vừa unpublish) làm default
                const nextDefault = updatedList.find(
                    b => b.id !== id && b.status === BANNER_STATUS.PUBLISHED
                ) || updatedList.find(b => b.id !== id);

                return updatedList.map(banner =>
                    banner.id === toggledBanner.id ?
                        { ...banner, isDefault: false } :
                        banner.id === nextDefault?.id ?
                            { ...banner, isDefault: true } :
                            { ...banner, isDefault: false });
            }

            return updatedList;
        });
    };

    const onDeleteBanner = (id: string) => {
        setBannerList((prev) => {
            const bannerToDelete = prev.find(banner => banner.id === id);

            // If the deleted banner is default, set another banner as default + publish
            if (bannerToDelete?.isDefault) {
                const newList = prev.filter(banner => banner.id !== id);

                if (newList.length === 0) {
                    // No banners left, just return empty
                    return [];
                }

                // Pick the first banner to be default + published
                const [first, ...rest] = newList;
                return [
                    { ...first, isDefault: true },
                    ...rest.map(banner => ({ ...banner, isDefault: false }))
                ];
            }

            // If not default, just remove
            return prev.filter(banner => banner.id !== id);
        });
    };

    const showAlertError = () => {
        showAlertModal({
            type: 'error',
            content: t('banner.image_input_error'),
            cancelButton: false,
            saveButton: t('button.close'),
            onSave: () => hideModal()
        });
    };

    const validateImageFile = (file: File): boolean => {
        const maxSize = 1000 * 1024;
        const isValidFormat = SUPPORTED_FORMATS.includes(file.type);
        if (file.size > maxSize || !isValidFormat) {
            showAlertError();
            return false;
        }
        return true;
    };

    const sortBannerList = (bannerList: IBanner[]) => {
        // unsaved banners should be at the top, then sort by createdAt descending
        return bannerList.slice().sort((a, b) => {
            if (a.status === BANNER_STATUS.UNSAVED && b.status !== BANNER_STATUS.UNSAVED) {
                return -1; // a comes first
            }
            if (b.status === BANNER_STATUS.UNSAVED && a.status !== BANNER_STATUS.UNSAVED) {
                return 1; // b comes first
            }
            // Both are same status, sort by createdAt descending (newest first)
            const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            return bTime - aTime;
        });
    };

    const onSubmit = async () => {
        const banners: any = [...bannerList.filter(banner => banner.status !== BANNER_STATUS.UNSAVED)];
        const unsavedBanners = bannerList.filter(banner => banner.status === BANNER_STATUS.UNSAVED).reverse();

        let presignedUrls = [];
        if (unsavedBanners.length > 0) {
            const res = await flowResult(getPresignedUrl(unsavedBanners.map(banner => ({ name: (banner.file as File).name, type: (banner.file as File).type }))));
            presignedUrls = res.presignedUrls || [];
        }
        for (let i = 0; i < unsavedBanners.length; i++) {
            const banner = unsavedBanners[i];
            const presignedUrl: any = presignedUrls[i];
            if (presignedUrl) {
                const file = banner.file as File;
                await uploadFileToS3UsingPresignedPost(file, presignedUrl);
                banners.push({
                    status: BANNER_STATUS.PUBLISHED,
                    key: presignedUrl.fields.key,
                    name: banner.name,
                    isDefault: banner.isDefault,
                    alt: banner.alt
                } as any);
            }
        }

        const res = await updateBanner({ banners });
        if (res) {
            toastify('success', t('messages.update_success'));
            refetchData();
        }
    };
    return (
        <>
            <div className='rounded h-full mt-4 bg-white py-4 px-5'>
                <div className=''>
                    <Input
                        id='shop-images'
                        type='file'
                        multiple
                        onChange={(event) => {
                            const files = event.target.files;
                            if (!files) return;
                            // Validate all files
                            for (let i = 0; i < files.length; i++) {
                                if (!validateImageFile(files[i])) {
                                    event.target.value = '';
                                    return;
                                }
                            }
                            // Add all files
                            setBannerList(prev => [
                                ...prev,
                                ...Array.from(files).map((file, idx) => ({
                                    id: (Date.now() + idx).toString(),
                                    name: file.name,
                                    image: URL.createObjectURL(file),
                                    status: BANNER_STATUS.UNSAVED,
                                    alt: '',
                                    isDefault: prev.length === 0 && idx === 0, // First image is default if no banners
                                    createdAt: new Date().toISOString(),
                                    file
                                }))
                            ]);
                        }}
                        className='hidden'
                        accept={SUPPORTED_FORMATS.join(', ')}
                    />
                    <Label
                        ref={labelImagesRef}
                        htmlFor='shop-images'
                        tabIndex={0}
                        className='cursor-pointer'
                    >
                        {previewImage ?
                            (
                                <div className='relative'>
                                    <img
                                        src={previewImage}
                                        alt='Preview'
                                        className='mx-auto h-[188px] w-[310px] rounded object-cover'
                                    />
                                    <div
                                        className='absolute right-1 top-1 cursor-pointer'
                                        tabIndex={-1}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            setPreviewImage(null);
                                        }}
                                    >
                                        { }
                                    </div>
                                </div>
                            ) :
                            (
                                <div
                                    className='relative border-2 border-dashed border-rose-300 rounded-md bg-gray-50 h-[150px] w-full text-center hover:bg-gray-100 transition flex flex-col justify-center items-center space-y-2 text-gray-700'
                                >
                                    <ImagePlusIcon className='text-active h-6 w-6 mr-2' />
                                    <span className='text-sm'>{t('button.add_shop_image')}</span>
                                    <div className='text-left'>
                                        <p className='text-sm'>{t('banner.recommended')}</p>
                                        <p className='text-sm'>{t('banner.file_type')}</p>
                                        <p className='text-sm'>{t('banner.max_size')}</p>
                                    </div>
                                </div>
                            )}
                    </Label>
                </div>
                <div className='border-b-2 border-dashed w-full h-0 mt-5 mb-4'></div>
                <div className='max-h-[800px] overflow-y-auto'>
                    {sortBannerList(bannerList).map(banner => (
                        <div key={banner.id} className='flex items-stretch justify-between mb-4 min-h-[130px]'>
                            <div className='flex space-x-4 w-5/12'>
                                <img
                                    src={banner.image}
                                    alt={banner.alt}
                                    className='h-[130px] w-[150px] object-contain rounded bg-[#F1F3F3]'
                                />
                                <div className='flex flex-col justify-between h-[130px]'>
                                    <div>
                                        <Tooltip
                                            align='start'
                                            content={banner.name}
                                            triggerClassName='cursor-default'
                                        >
                                            <p className='text-[15px] font-semibold mb-[10px] truncate max-w-[200px]'>{banner.name}</p>
                                        </Tooltip>
                                        <div className='flex'>{renderImageStatus(banner.status, t)}</div>
                                    </div>
                                    <div>
                                        {renderDefaultBanner(banner, t)}
                                    </div>
                                </div>
                            </div>
                            <div className='w-6/12 flex flex-col justify-start '>
                                <p className='text-[#777777]'>Alt text</p>
                                <Textarea
                                    className='mt-[5px]'
                                    defaultValue={banner.alt}
                                    onChange={(e) => {
                                        onChangeAlt(banner.id, e.target.value);
                                    }}
                                    rows={4}
                                />
                            </div>
                            <div className='w-1/12 min-h-[130px] flex items-center justify-center'>
                                <ActionMenuDropdown banner={banner} onDeleteBanner={onDeleteBanner} onTogglePublishBanner={onTogglePublishBanner} setDefaultBanner={setDefaultBanner} />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <div className='flex justify-center items-center py-6 space-x-4 '>
                <Button variant='submit' className='mt-6 min-w-60' onClick={onSubmit}>
                    {t('button.save')}
                </Button>
            </div>
        </>
    );
});
